<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import type { InsightsFilterOptions, InsightsScope } from '@/lib/insights'

/**
 * The Region → Brand → Line → Tier cascade, plus Storage and Grade.
 *
 * Each pick is emitted as the one level that changed; the page resets the levels
 * below it, since their options come from the level above (`/filters`). "All" is a
 * sentinel because a select item can't carry an empty value.
 */
const props = defineProps<{
    options: InsightsFilterOptions
    scope: InsightsScope
}>()

const emit = defineEmits<{
    change: [level: keyof InsightsScope, value: string | null]
}>()

const { t } = useI18n()

const ALL = '__all'

const toValue = (value: string | number | null): string => (value === null ? ALL : String(value))

const onChange = (level: keyof InsightsScope, value: unknown) => {
    const picked = String(value)

    emit('change', level, picked === ALL ? null : picked)
}

/**
 * A region and the countries inside it, in one list. A region of one country (CH)
 * doesn't repeat itself as a country: both would show the same numbers.
 */
const geoOptions = computed(() =>
    props.options.regions.flatMap((region) => [
        { geo: region.geo, label: region.label, indent: false },
        ...(region.countries.length > 1
            ? region.countries.map((country) => ({
                  geo: country.geo,
                  label: country.label,
                  indent: true,
                  region: region.label,
              }))
            : []),
    ]),
)

/** "EU · Italy" in the closed select, so the region stays readable once a country is picked. */
const geoLabel = computed(() => {
    for (const region of props.options.regions) {
        if (region.geo === props.scope.geo) {
            return region.label
        }

        const country = region.countries.find((candidate) => candidate.geo === props.scope.geo)

        if (country) {
            return `${region.label} · ${country.label}`
        }
    }

    return ''
})

const levels = computed(() => [
    {
        level: 'line' as const,
        label: t('insights.filters.line'),
        all: t('insights.filters.allLines'),
        options: props.options.lines,
        show: true,
    },
    {
        level: 'tier' as const,
        label: t('insights.filters.tier'),
        all: t('insights.filters.allTiers'),
        options: props.options.tiers,
        // A tier only exists within a line.
        show: props.scope.line !== null && props.options.tiers.length > 0,
    },
    {
        level: 'storage' as const,
        label: t('insights.filters.storage'),
        all: t('insights.filters.allStorages'),
        options: props.options.storages,
        show: true,
    },
    {
        level: 'grade' as const,
        label: t('insights.filters.grade'),
        all: t('insights.filters.allGrades'),
        options: props.options.grades,
        show: true,
    },
])
</script>

<template>
    <div class="flex flex-wrap items-end gap-4">
        <div class="grid gap-1.5">
            <Label class="text-xs text-muted-foreground">{{ t('insights.filters.region') }}</Label>
            <Select :model-value="scope.geo" @update:model-value="onChange('geo', $event)">
                <SelectTrigger class="h-9 w-48">
                    <SelectValue>{{ geoLabel }}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem
                        v-for="option in geoOptions"
                        :key="option.geo"
                        :value="option.geo"
                        :class="option.indent ? 'pl-6 text-muted-foreground' : 'font-medium'"
                    >
                        {{ option.label }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>

        <div class="grid gap-1.5">
            <Label class="text-xs text-muted-foreground">{{ t('insights.filters.brand') }}</Label>
            <Select :model-value="String(scope.brand_id)" @update:model-value="onChange('brand_id', $event)">
                <SelectTrigger class="h-9 w-40">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="brand in options.brands" :key="brand.id" :value="String(brand.id)">
                        {{ brand.name }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>

        <template v-for="level in levels" :key="level.level">
            <div v-if="level.show" class="grid gap-1.5">
                <Label class="text-xs text-muted-foreground">{{ level.label }}</Label>
                <Select :model-value="toValue(scope[level.level])" @update:model-value="onChange(level.level, $event)">
                    <SelectTrigger class="h-9 w-48">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem :value="ALL">{{ level.all }}</SelectItem>
                        <SelectItem v-for="option in level.options" :key="option.code" :value="option.code">
                            {{ option.label }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </template>
    </div>
</template>
